import React from 'react';

function LendingCompliance() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* CRITICAL DISCLAIMER - NOT A DIRECT LENDER */}
        <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-bold text-red-800 mb-2">
                IMPORTANT: Aramco Leads Pro is NOT a Direct Lender
              </h3>
              <p className="text-red-700 mb-2">
                <strong>We are a lead generation and CRM platform.</strong> We do not make loans, extend credit, 
                or make lending decisions. We connect consumers with licensed lending partners who are independent 
                financial institutions.
              </p>
              <p className="text-sm text-red-600">
                All lending decisions, loan terms, interest rates, and approvals are made solely by our partner 
                lenders, not by Aramco Leads Pro. We do not have access to your credit information and do not 
                participate in the underwriting process.
              </p>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            Lending & Financial Services Compliance
          </h1>
          <p className="text-xl text-gray-600">
            Our commitment to responsible lending practices and consumer protection
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Lead Generation Platform | Not a Direct Lender
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white shadow-lg rounded-lg p-8 space-y-8">
          
          {/* Lending Services Disclosure */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Lending Services Disclosure
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                Aramco Leads Pro provides information about lending and financial services. 
                By opting in to receive SMS messages about lending services, you acknowledge 
                and agree to the following:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Promotional Messages:</strong> You consent to receive promotional 
                  messages about loan products, lending opportunities, and financial services
                </li>
                <li>
                  <strong>Message Content:</strong> Messages may include information about 
                  interest rates, loan terms, eligibility requirements, and special offers
                </li>
                <li>
                  <strong>Not a Direct Lender:</strong> We are not a direct lender but may 
                  connect you with licensed lending partners
                </li>
                <li>
                  <strong>No Guarantee:</strong> Receiving messages does not guarantee loan 
                  approval or specific terms
                </li>
                <li>
                  <strong>Credit Approval:</strong> All loan offers are subject to credit 
                  approval and underwriting by the lender
                </li>
                <li>
                  <strong>Variable Terms:</strong> APR, terms, and conditions vary by lender 
                  and your creditworthiness
                </li>
                <li>
                  <strong>Right to Opt Out:</strong> You have the right to opt out at any 
                  time by replying STOP, without affecting any existing loan or service
                </li>
              </ul>
            </div>
          </section>

          {/* Regulatory Compliance */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Regulatory Compliance
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4">
              <p className="text-gray-700">
                We are committed to full compliance with all applicable federal and state 
                lending regulations.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Federal Compliance
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>✅ Truth in Lending Act (TILA)</li>
                  <li>✅ Equal Credit Opportunity Act (ECOA)</li>
                  <li>✅ Fair Credit Reporting Act (FCRA)</li>
                  <li>✅ CFPB Guidelines</li>
                  <li>✅ TCPA Compliance</li>
                  <li>✅ CAN-SPAM Act</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Consumer Protection
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li>✅ Fair Lending Practices</li>
                  <li>✅ No Predatory Lending</li>
                  <li>✅ Transparent Terms</li>
                  <li>✅ Clear Disclosures</li>
                  <li>✅ Privacy Protection</li>
                  <li>✅ Data Security</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Truth in Lending Disclosures */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Truth in Lending Disclosures
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                All loan offers communicated through our platform include the following 
                required disclosures:
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Required Loan Disclosures
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Annual Percentage Rate (APR):</strong> The cost of credit 
                    expressed as a yearly rate
                  </li>
                  <li>
                    <strong>Loan Amount:</strong> The principal amount being borrowed
                  </li>
                  <li>
                    <strong>Repayment Terms:</strong> Duration and schedule of payments
                  </li>
                  <li>
                    <strong>Finance Charges:</strong> Total cost of the loan including 
                    interest and fees
                  </li>
                  <li>
                    <strong>Total Amount Payable:</strong> Sum of all payments over the 
                    loan term
                  </li>
                  <li>
                    <strong>Lender Information:</strong> Name and contact information of 
                    the lending institution
                  </li>
                </ul>
              </div>
              
              <p className="text-sm italic text-gray-600 mt-4">
                All loan offers are subject to credit approval. Terms and conditions vary 
                by lender and individual creditworthiness. Not all applicants will qualify 
                for the lowest rates or maximum loan amounts.
              </p>
            </div>
          </section>

          {/* SMS Consent for Lending */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              SMS Consent for Lending Messages
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                When you opt in to receive SMS messages about lending services, you are 
                providing express written consent to receive:
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-900">Informational Messages</h4>
                  <p className="text-sm text-gray-700">
                    Educational content about loan products, interest rates, and lending 
                    options
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Promotional Offers</h4>
                  <p className="text-sm text-gray-700">
                    Pre-qualified loan offers and financing opportunities based on your 
                    profile
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Application Updates</h4>
                  <p className="text-sm text-gray-700">
                    Status notifications for loan inquiries or applications you've initiated
                  </p>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900">Account Notifications</h4>
                  <p className="text-sm text-gray-700">
                    Important updates about existing lending relationships
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-100 p-6 rounded-lg mt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Message Frequency & Costs</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>
                    <strong>Frequency:</strong> Message frequency varies, up to 10 messages 
                    per month
                  </li>
                  <li>
                    <strong>Costs:</strong> Message and data rates may apply based on your 
                    mobile carrier plan
                  </li>
                  <li>
                    <strong>Opt-Out:</strong> Reply STOP to any message to opt out immediately
                  </li>
                  <li>
                    <strong>Help:</strong> Reply HELP for assistance or contact information
                  </li>
                  <li>
                    <strong>Not Required:</strong> Consent is not a condition of obtaining 
                    any loan or financial service
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Fair Lending Practices */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Fair Lending Practices
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                We are committed to fair lending practices and equal opportunity for all 
                consumers:
              </p>
              
              <div className="bg-green-50 border-l-4 border-green-500 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Equal Credit Opportunity
                </h3>
                <p className="text-gray-700 mb-3">
                  We do not discriminate on the basis of:
                </p>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Race or color</li>
                  <li>Religion</li>
                  <li>National origin</li>
                  <li>Sex or gender</li>
                  <li>Marital status</li>
                  <li>Age (provided you have the capacity to contract)</li>
                  <li>Receipt of public assistance</li>
                </ul>
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                All lending decisions are based solely on creditworthiness, ability to 
                repay, and other legitimate financial factors.
              </p>
            </div>
          </section>

          {/* Privacy & Data Security */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Privacy & Data Security for Lending
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                We take the security of your financial information seriously:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Data Protection</h4>
                  <ul className="text-sm space-y-1 text-gray-700">
                    <li>✅ Encrypted data transmission</li>
                    <li>✅ Secure database storage</li>
                    <li>✅ Limited access controls</li>
                    <li>✅ Regular security audits</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Your Rights</h4>
                  <ul className="text-sm space-y-1 text-gray-700">
                    <li>✅ Access your data</li>
                    <li>✅ Request corrections</li>
                    <li>✅ Request deletion</li>
                    <li>✅ Opt out anytime</li>
                  </ul>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mt-4">
                For more information, see our complete{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </section>

          {/* OUR BUSINESS MODEL - NOT A LENDER */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Our Business Model: Lead Generation Platform
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                What We Do
              </h3>
              <p className="text-blue-800 mb-3">
                Aramco Leads Pro operates as a <strong>lead generation and customer relationship management (CRM) platform</strong>. 
                Our role is to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-blue-800">
                <li>Connect consumers interested in financial services with licensed lending partners</li>
                <li>Provide information about available loan products and financing options</li>
                <li>Facilitate communication between consumers and lenders</li>
                <li>Manage customer inquiries and application status updates</li>
              </ul>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3">
                What We Do NOT Do
              </h3>
              <ul className="list-disc pl-6 space-y-2 text-red-800">
                <li><strong>We do NOT make loans</strong> or extend credit to consumers</li>
                <li><strong>We do NOT make lending decisions</strong> or approve/deny loan applications</li>
                <li><strong>We do NOT set interest rates</strong>, loan terms, or repayment schedules</li>
                <li><strong>We do NOT access credit reports</strong> or perform credit checks</li>
                <li><strong>We do NOT participate in underwriting</strong> or loan servicing</li>
                <li><strong>We do NOT collect loan payments</strong> or manage loan accounts</li>
              </ul>
            </div>

            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p className="text-lg font-semibold text-gray-900">
                All lending activities are performed by licensed financial institutions, not by Aramco Leads Pro.
              </p>
            </div>
          </section>

          {/* Partner Lenders */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Partner Lenders
            </h2>
            <div className="prose prose-blue max-w-none text-gray-700 space-y-4">
              <p>
                Aramco Leads Pro works with a network of licensed lending partners. When 
                you express interest in a loan product:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  We may share your information with one or more licensed lending partners
                </li>
                <li>
                  Each partner lender is independently licensed and regulated by state and federal authorities
                </li>
                <li>
                  <strong>All lending decisions are made by the partner lender, not by Aramco Leads Pro</strong>
                </li>
                <li>
                  You may be contacted directly by partner lenders regarding your loan inquiry
                </li>
                <li>
                  You are not obligated to accept any loan offer or work with any specific lender
                </li>
                <li>
                  Partner lenders are responsible for all loan terms, disclosures, and servicing
                </li>
              </ul>
              
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mt-4">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Each lender has its own terms, conditions, 
                  and privacy policy. Please review these carefully before accepting any 
                  loan offer. Aramco Leads Pro is not responsible for the actions, terms, 
                  or practices of partner lenders.
                </p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Questions or Concerns?
            </h2>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                If you have questions about our lending services, compliance practices, 
                or your rights as a consumer:
              </p>
              
              <div className="space-y-2 text-gray-700">
                <p>
                  <strong>Email:</strong>{' '}
                  <a href="mailto:compliance@aramcoleadspro.com" className="text-blue-600 hover:underline">
                    compliance@aramcoleadspro.com
                  </a>
                </p>
                <p>
                  <strong>Phone:</strong> Available on our{' '}
                  <a href="/contact" className="text-blue-600 hover:underline">
                    Contact page
                  </a>
                </p>
                <p>
                  <strong>Mail:</strong> 11 Linden Ave, Kearny, NJ 07032, United States
                </p>
              </div>
            </div>
          </section>

          {/* Last Updated */}
          <div className="text-center text-sm text-gray-500 pt-8 border-t">
            Last Updated: December 2, 2025
          </div>
        </div>
      </div>
    </div>
  );
}

export default LendingCompliance;
